import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import RingRequestForm from "@/components/RingRequestForm";
import api from "@/lib/api";

export default function Collection() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const metal = sp.get("metal");
  const { storefront } = useApp();
  const [products, setProducts] = useState([]);
  const [collection, setCollection] = useState(null);
  const [promo, setPromo] = useState({});
  const [ringOpen, setRingOpen] = useState(false);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    const params = { collection: slug };
    if (metal) params.metal = metal;
    api.get(`/storefront/${storefront}/products`, { params }).then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get(`/storefront/${storefront}/bootstrap`).then((r) => {
      setCollection((r.data.collections || []).find((c) => c.slug === slug) || { name: slug, description: "" });
      setPromo(r.data.promo || {});
    });
  }, [storefront, slug, metal]);

  const sorted = [...products].sort((a, b) => {
    const pa = a.sale_price_usd || a.price_usd, pb = b.sale_price_usd || b.price_usd;
    if (sort === "low") return pa - pb;
    if (sort === "high") return pb - pa;
    return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
  });

  const wide = storefront === "jewelry" && slug === "gifts";
  const isEng = slug === "engagement-rings";

  return (
    <div>
      {collection && (
        <section className="text-center px-6 pt-16 pb-10 max-w-3xl mx-auto">
          <h1 className="font-serif text-3xl md:text-5xl capitalize mb-4">{collection.name}</h1>
          {collection.description && <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--ev-muted)" }}>{collection.description}</p>}
        </section>
      )}

      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-10 border-b pb-5" style={{ borderColor: "var(--ev-border)" }}>
          <span className="text-sm" style={{ color: "var(--ev-muted)" }}>{sorted.length} {sorted.length === 1 ? "piece" : "pieces"}</span>
          <select data-testid="collection-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="bg-transparent text-sm tracking-wide uppercase text-[0.7rem] outline-none cursor-pointer">
            <option value="featured">Featured</option>
            <option value="low">Price: Low to High</option>
            <option value="high">Price: High to Low</option>
          </select>
        </div>

        {sorted.length === 0 ? (
          <div className="text-center py-28">
            <p className="font-serif text-3xl mb-3">This collection is being curated</p>
            <p style={{ color: "var(--ev-muted)" }}>New pieces will arrive here shortly.</p>
          </div>
        ) : isEng ? (
          <div className="lg:flex lg:gap-10 lg:items-start">
            {promo?.enabled && (
              <aside data-testid="ring-promo-panel" className="lg:w-72 lg:sticky lg:top-28 h-fit mb-8 border p-6"
                style={{ borderColor: "var(--ev-border)", background: "var(--ev-surface)" }}>
                {promo.image && <img src={promo.image} alt="" className="w-full h-32 object-cover mb-4" />}
                <h3 className="font-serif text-xl mb-3 leading-snug">{promo.heading}</h3>
                <p className="text-sm mb-5 leading-relaxed" style={{ color: "var(--ev-muted)" }}>{promo.description}</p>
                <button data-testid="open-ring-form" onClick={() => setRingOpen(true)} className="btn-luxe w-full" style={{ fontSize: "0.6rem", padding: "0.85rem 1rem" }}>{promo.button_text || "Create Your Bespoke Ring"}</button>
              </aside>
            )}
            <div className="flex-1 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
              {sorted.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 80}><ProductCard product={p} /></Reveal>)}
            </div>
          </div>
        ) : wide ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
            {sorted.map((p, i) => <Reveal key={p.id} delay={(i % 2) * 80}><ProductCard product={p} wide /></Reveal>)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
            {sorted.map((p, i) => <Reveal key={p.id} delay={(i % 4) * 80}><ProductCard product={p} /></Reveal>)}
          </div>
        )}
      </div>
      <RingRequestForm open={ringOpen} onClose={() => setRingOpen(false)} promo={promo} />
    </div>
  );
}
